import {
  Arrow,
  Icon,
  IMG,
  PageHero,
  Reveal,
  Section,
  SectionHeader,
  TextLink,
  FinalCta,
} from "../lib/shared"
import {
  BentoCard,
  IntegrateVisual,
  PortalVisual,
  ReconcileVisual,
  RecordsVisual,
  SitesVisual,
  StepFlow,
  TrackerVisual,
} from "../lib/visuals"
import { useCollection } from "../lib/content"
import { Seo, breadcrumbSchema } from "../lib/seo"

const SOLUTIONS = [
  {
    t: "Citizen and service portals",
    d: "Online applications, status tracking and notifications for services people currently queue for.",
    visual: <PortalVisual />,
    span: "lg:col-span-7",
    tone: "accent",
  },
  {
    t: "Records and case management",
    d: "Structured records with clear ownership, search and a history of every change.",
    visual: <RecordsVisual />,
    span: "lg:col-span-5",
    tone: "ink",
  },
  {
    t: "Licensing and permits",
    d: "Application, review, approval and verification, carried out in one accountable workflow.",
    visual: <TrackerVisual />,
    span: "lg:col-span-5",
    tone: "mist",
  },
  {
    t: "Revenue and payment tracking",
    d: "Payment records and bank statements reconciled, with exceptions visible for review.",
    visual: <ReconcileVisual />,
    span: "lg:col-span-7",
    tone: "glow",
  },
  {
    t: "Multi-site reporting",
    d: "Consistent data from state, zonal and field offices, with scheduled management reports.",
    visual: <SitesVisual />,
    span: "lg:col-span-7",
    tone: "ink",
  },
  {
    t: "Integration and data migration",
    d: "Existing systems connected, and paper or spreadsheet records moved across with checks.",
    visual: <IntegrateVisual />,
    span: "lg:col-span-5",
    tone: "accent",
  },
] as const

/* Our enterprise work, read across to the public-sector problem it resembles.
   Worded as a similarity on purpose: this is not a list of government projects. */
const CARRIES_OVER = [
  {
    built: "Operations across several locations",
    applies: "Service delivery across state and zonal offices",
  },
  {
    built: "Staged approvals with an audit log",
    applies: "Accountable approvals and reviewable records",
  },
  {
    built: "Payment confirmation and bank reconciliation",
    applies: "Revenue collection and remittance tracking",
  },
  {
    built: "Ticketing and security checks at the gate",
    applies: "Verification at the point of service or inspection",
  },
  {
    built: "Automated daily management reports",
    applies: "Scheduled reporting to senior officers",
  },
  {
    built: "SMS and WhatsApp notifications",
    applies: "Status updates to the public and to staff",
  },
]

const ROUTES = [
  {
    n: "01",
    t: "Direct procurement",
    d: "Send us your request for proposal, expression of interest or tender notice. Tell us which documents the process requires and we will confirm what we can provide.",
  },
  {
    n: "02",
    t: "Partnering on a larger programme",
    d: "We can act as the software engineering partner to a prime contractor, consultant or implementing organisation.",
  },
  {
    n: "03",
    t: "A scoped first phase",
    d: "Start with a requirements study or pilot in one department, then decide on wider rollout with evidence from real use.",
  },
]

const COMMITMENTS = [
  "Requirements, scope and acceptance criteria agreed in writing",
  "Data protection, hosting and access requirements settled at the start",
  "Documentation and training as part of handover",
  "Maintenance and support arrangements agreed before launch",
]

export default function Government() {
  const { items } = useCollection("caseStudies")
  const reference = items.find(
    (w) => w.slug === "soroman-energy" && w.status === "Published",
  )

  return (
    <>
      <Seo
        title="Government and institutional technology"
        description="Sableboxx designs, builds and implements digital systems for government agencies and public institutions in Nigeria: service portals, records, licensing, revenue tracking and reporting."
        jsonLd={[
          breadcrumbSchema([{ name: "Government", path: "/government" }]),
        ]}
      />
      <PageHero
        eyebrow="Government and institutions"
        title="Digital systems for the institutions people rely on"
        intro="We design, build and implement systems that help ministries, agencies and public institutions manage information, automate operations and deliver services more efficiently."
        image={IMG.processHero}
      />

      <Section tone="paper">
        <SectionHeader
          eyebrow="What we can build"
          title="Technology solutions for public service"
          intro="Each is scoped to the institution's mandate, its existing systems and the people who will use it."
        />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          {SOLUTIONS.map((s, i) => (
            <Reveal key={s.t} delay={(i % 2) * 90} className={s.span}>
              <BentoCard tone={s.tone} title={s.t} text={s.d} visual={s.visual} />
            </Reveal>
          ))}
        </div>
      </Section>

      <Section tone="ink">
        <SectionHeader
          light
          eyebrow="Our experience"
          title="Enterprise engineering that carries over"
          intro="Our largest reference system so far is in the private sector: an operations platform for a Nigerian energy company. The problems it solves are close to the ones public institutions face."
        />
        <div className="overflow-hidden rounded-2xl border border-white/10">
          <div className="hidden grid-cols-[1fr_2rem_1fr] gap-6 border-b border-white/10 bg-white/[0.04] px-7 py-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-neutral-400 sm:grid">
            <span>What we have built</span>
            <span />
            <span>Where the same engineering applies</span>
          </div>
          {CARRIES_OVER.map((c) => (
            <div
              key={c.built}
              className="group grid grid-cols-1 gap-1.5 border-b border-white/10 px-7 py-5 transition-colors duration-300 last:border-b-0 hover:bg-white/[0.05] sm:grid-cols-[1fr_2rem_1fr] sm:items-center sm:gap-6"
            >
              <p className="text-[16px] font-semibold text-white">{c.built}</p>
              <Arrow className="hidden h-4 w-4 text-accent-bright transition-transform duration-300 group-hover:translate-x-1.5 sm:block" />
              <p className="text-[15px] leading-[1.6] text-neutral-400 transition-colors duration-300 group-hover:text-white">
                {c.applies}
              </p>
            </div>
          ))}
        </div>
        {reference && (
          <Reveal>
            <div className="mt-10">
              <TextLink light to={`/work/${reference.slug}`}>
                Read the {reference.client} case study
              </TextLink>
            </div>
          </Reveal>
        )}
      </Section>

      <Section tone="mist">
        <SectionHeader
          eyebrow="How to engage us"
          title="Three ways to start"
          intro="We work within your procurement process, whichever route it takes."
        />
        <StepFlow steps={ROUTES} className="lg:grid-cols-3" />
      </Section>

      <Section tone="paper">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeader
              layout="stacked"
              eyebrow="On every engagement"
              title="What you can hold us to"
            />
          </div>
          <ul className="space-y-4 lg:col-span-7">
            {COMMITMENTS.map((c) => (
              <li
                key={c}
                className="flex items-start gap-3 border-b border-neutral-200 pb-4 text-[17px] leading-[1.6] text-ink transition-transform duration-300 last:border-b-0 hover:translate-x-1"
              >
                <Icon
                  name="check"
                  className="mt-1 h-5 w-5 shrink-0 text-accent"
                />
                {c}
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <FinalCta />
    </>
  )
}
